import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { 
  Truck, Phone, MapPin, Package, LogOut, CheckCircle, Clock, 
  Navigation, MessageCircle, XCircle, RotateCcw, UserX, 
  DollarSign, TrendingUp, Calendar, MapPinned, ExternalLink, RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_bahr-rimal-system/artifacts/xdruqubd_IMG_6467.JPG";

const DriverDashboard = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("deliveries");
  const [actionDialog, setActionDialog] = useState({ open: false, order: null, action: null });
  const [actionReason, setActionReason] = useState("");
  const [driverLocation, setDriverLocation] = useState("");
  const [locationSaving, setLocationSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDeliveries = useCallback(async (showToast = false) => {
    try {
      const res = await axios.get(`${API}/driver/deliveries`, { headers: getAuthHeaders() });
      setDeliveries(res.data);
      if (showToast) toast.success(isRTL ? "تم التحديث" : "Refreshed");
    } catch (error) {
      console.error("Failed to fetch deliveries:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, isRTL]);

  const fetchEarnings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/drivers/my-earnings`, { headers: getAuthHeaders() });
      setEarnings(res.data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
    }
  }, [getAuthHeaders]);

  const fetchLocation = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/driver/location`, { headers: getAuthHeaders() });
      setDriverLocation(res.data.location || "");
    } catch (error) {
      console.error("Failed to fetch location:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchDeliveries();
    fetchEarnings();
    fetchLocation();
  }, [fetchDeliveries, fetchEarnings, fetchLocation]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchDeliveries(false), 15000);
    return () => clearInterval(interval);
  }, [fetchDeliveries]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDeliveries(true), fetchEarnings()]);
  };

  const saveLocation = async () => {
    if (!driverLocation.trim()) {
      toast.error(isRTL ? "الرجاء إدخال موقعك" : "Please enter your location");
      return;
    }
    setLocationSaving(true);
    try {
      await axios.put(`${API}/driver/location`, { location: driverLocation }, { headers: getAuthHeaders() });
      toast.success(isRTL ? "تم تحديث الموقع" : "Location updated");
    } catch (error) {
      toast.error(isRTL ? "فشل في تحديث الموقع" : "Failed to update location");
    } finally {
      setLocationSaving(false);
    }
  };

  const openLocationInMaps = () => {
    if (driverLocation) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(driverLocation)}`, '_blank');
    }
  };

  const handleDriverAction = async (orderId, action, reason = null) => {
    try {
      const params = new URLSearchParams({ action });
      if (reason) params.append('reason', reason);
      
      await axios.post(
        `${API}/orders/${orderId}/driver-action?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      const messages = {
        delivered: t("orderDelivered"),
        cancelled: t("orderCancelled"),
        returned: t("orderReturned"),
        customer_not_found: t("customerNotFound")
      };
      
      toast.success(messages[action] || t("orderUpdated"));
      setActionDialog({ open: false, order: null, action: null });
      setActionReason("");
      fetchDeliveries();
      fetchEarnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToUpdateStatus"));
    }
  };

  const openActionDialog = (order, action) => {
    if (action === "delivered") {
      // Direct delivery confirmation
      handleDriverAction(order.id, "delivered");
    } else {
      // Open dialog for reason
      setActionDialog({ open: true, order, action });
    }
  };

  const confirmAction = () => {
    if (actionDialog.order && actionDialog.action) {
      handleDriverAction(actionDialog.order.id, actionDialog.action, actionReason);
    }
  };

  const getGoogleMapsLink = (address) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const getPhoneCallLink = (phone) => {
    return `tel:${phone?.replace(/\D/g, "")}`;
  };

  const getWhatsAppLink = (phone, order) => {
    const message = encodeURIComponent(
      `مرحباً، أنا سائق التوصيل من شركة بحر الرمال. أنا في الطريق لتوصيل طلبك رقم #${order.id.slice(0, 8)}.`
    );
    return `https://wa.me/${phone?.replace(/\D/g, "")}?text=${message}`;
  };

  const handleLogout = () => {
    logout();
    navigate("/driver/login");
  };

  const statusColors = {
    pending: "bg-yellow-500",
    processing: "bg-blue-500",
    out_for_delivery: "bg-orange-500",
    delivered: "bg-green-500",
    cancelled: "bg-red-500",
    returned: "bg-purple-500",
    customer_not_found: "bg-gray-500"
  };

  const statusLabels = {
    pending: t("pending"),
    processing: t("processing"),
    out_for_delivery: t("outForDelivery"),
    delivered: t("delivered"),
    cancelled: t("cancelled"),
    returned: isRTL ? "مرتجع" : "Returned",
    customer_not_found: isRTL ? "لم يتم العثور على العميل" : "Customer Not Found"
  };

  const actionLabels = {
    cancelled: isRTL ? "سبب الإلغاء" : "Cancellation Reason",
    returned: isRTL ? "سبب الإرجاع" : "Return Reason",
    customer_not_found: isRTL ? "ملاحظات" : "Notes"
  };

  const activeDeliveries = deliveries.filter(d => d.status === "out_for_delivery");
  const completedToday = deliveries.filter(d => d.status === "delivered").length;

  return (
    <div className="min-h-screen bg-[#0f172a]" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full" />
              <div>
                <h1 className="font-heading text-lg text-white">{isRTL ? "بوابة السائقين" : "Driver Portal"}</h1>
                <p className="text-xs text-slate-400">{user?.full_name || user?.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleRefresh} 
                disabled={refreshing}
                className="text-[#d4af37] hover:text-[#d4af37]/80"
                data-testid="refresh-deliveries-btn"
              >
                <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400 hover:text-white">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <Package className="h-6 w-6 text-orange-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{activeDeliveries.length}</p>
              <p className="text-xs text-slate-400">{isRTL ? "قيد التوصيل" : "Active"}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 text-green-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{completedToday}</p>
              <p className="text-xs text-slate-400">{isRTL ? "تم اليوم" : "Today"}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 text-[#d4af37] mx-auto mb-2" />
              <p className="text-2xl font-bold text-white">{earnings?.pending_amount?.toFixed(0) || 0}</p>
              <p className="text-xs text-slate-400">{isRTL ? "د.ل مستحق" : "LYD Due"}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "deliveries" ? "default" : "outline"}
            onClick={() => setActiveTab("deliveries")}
            className={activeTab === "deliveries" ? "bg-[#d4af37] text-[#0f172a]" : "border-slate-600 text-slate-300"}
          >
            <Truck className="h-4 w-4 mr-2" />
            {isRTL ? "التوصيلات" : "Deliveries"}
          </Button>
          <Button
            variant={activeTab === "earnings" ? "default" : "outline"}
            onClick={() => setActiveTab("earnings")}
            className={activeTab === "earnings" ? "bg-[#d4af37] text-[#0f172a]" : "border-slate-600 text-slate-300"}
          >
            <TrendingUp className="h-4 w-4 mr-2" />
            {isRTL ? "الأرباح" : "Earnings"}
          </Button>
        </div>

        {/* Driver Location Card */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPinned className="h-5 w-5 text-[#d4af37]" />
              <h3 className="text-white font-medium">{isRTL ? "موقعي الحالي" : "My Current Location"}</h3>
            </div>
            <div className="flex gap-2">
              <Input
                value={driverLocation}
                onChange={(e) => setDriverLocation(e.target.value)}
                placeholder={isRTL ? "أدخل موقعك الحالي..." : "Enter your current location..."}
                className="bg-slate-700 border-slate-600 text-white flex-1"
                data-testid="driver-location-input"
              />
              <Button
                onClick={saveLocation}
                disabled={locationSaving}
                className="bg-[#d4af37] text-[#0f172a] hover:bg-[#d4af37]/90"
                data-testid="save-location-btn"
              >
                {locationSaving ? (isRTL ? "حفظ..." : "Saving...") : (isRTL ? "حفظ" : "Save")}
              </Button>
              {driverLocation && (
                <Button
                  onClick={openLocationInMaps}
                  variant="outline"
                  className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  data-testid="open-maps-btn"
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Deliveries Tab */}
        {activeTab === "deliveries" && (
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-slate-400">{t("loading")}</div>
            ) : activeDeliveries.length === 0 ? (
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="py-12 text-center">
                  <Truck className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">{isRTL ? "لا توجد توصيلات حالياً" : "No active deliveries"}</p>
                </CardContent>
              </Card>
            ) : (
              activeDeliveries.map((order) => (
                <Card key={order.id} className="bg-slate-800 border-slate-700 overflow-hidden">
                  <CardHeader className="bg-slate-700/50 py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={`${statusColors[order.status]} text-white`}>
                          {statusLabels[order.status]}
                        </Badge>
                        <span className="text-white font-mono text-sm">#{order.id.slice(0, 8)}</span>
                      </div>
                      <span className="text-[#d4af37] font-bold">{order.total} {t("lyd")}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 space-y-4">
                    {/* Customer Info */}
                    <div className="flex items-start gap-3">
                      <div className="bg-slate-700 p-2 rounded-lg">
                        <Package className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{order.customer_name}</p>
                        <p className="text-slate-400 text-sm">{order.items?.length || 0} {t("items")}</p>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="flex items-start gap-3">
                      <div className="bg-slate-700 p-2 rounded-lg">
                        <MapPin className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-slate-300 text-sm">{order.customer_address}</p>
                        <a
                          href={getGoogleMapsLink(order.customer_address)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-400 text-xs mt-1 hover:underline"
                        >
                          <Navigation className="h-3 w-3" />
                          {isRTL ? "فتح في الخرائط" : "Open in Maps"}
                        </a>
                      </div>
                    </div>

                    {/* Contact Buttons */}
                    <div className="grid grid-cols-2 gap-2">
                      <a href={getPhoneCallLink(order.customer_phone)} className="block">
                        <Button className="w-full bg-green-600 hover:bg-green-700" data-testid={`call-${order.id}`}>
                          <Phone className="h-4 w-4 mr-2" />
                          {isRTL ? "اتصال" : "Call"}
                        </Button>
                      </a>
                      <a href={getWhatsAppLink(order.customer_phone, order)} target="_blank" rel="noopener noreferrer" className="block">
                        <Button variant="outline" className="w-full border-green-500 text-green-400 hover:bg-green-500/10">
                          <MessageCircle className="h-4 w-4 mr-2" />
                          {isRTL ? "واتساب" : "WhatsApp"}
                        </Button>
                      </a>
                    </div>

                    {/* Action Buttons */}
                    <div className="border-t border-slate-700 pt-4 space-y-2">
                      <Button
                        className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0f172a] font-semibold"
                        onClick={() => openActionDialog(order, "delivered")}
                        data-testid={`deliver-${order.id}`}
                      >
                        <CheckCircle className="h-5 w-5 mr-2" />
                        {isRTL ? "تم التوصيل" : "Mark Delivered"}
                      </Button>
                      
                      <div className="grid grid-cols-3 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-400 hover:bg-red-500/10"
                          onClick={() => openActionDialog(order, "cancelled")}
                          data-testid={`cancel-${order.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          {isRTL ? "إلغاء" : "Cancel"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                          onClick={() => openActionDialog(order, "returned")}
                          data-testid={`return-${order.id}`}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          {isRTL ? "إرجاع" : "Return"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-gray-500 text-gray-400 hover:bg-gray-500/10"
                          onClick={() => openActionDialog(order, "customer_not_found")}
                          data-testid={`not-found-${order.id}`}
                        >
                          <UserX className="h-4 w-4 mr-1" />
                          {isRTL ? "لم أجده" : "Not Found"}
                        </Button>
                      </div>
                    </div>

                    {/* Order Notes */}
                    {order.notes && (
                      <div className="bg-slate-700/50 p-3 rounded-lg">
                        <p className="text-xs text-slate-400 mb-1">{t("notes")}:</p>
                        <p className="text-slate-300 text-sm">{order.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === "earnings" && (
          <div className="space-y-4">
            {/* Earnings Summary */}
            <Card className="bg-gradient-to-br from-[#d4af37]/20 to-slate-800 border-[#d4af37]/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-heading text-lg">{isRTL ? "ملخص الأرباح" : "Earnings Summary"}</h3>
                  <Badge className="bg-[#d4af37] text-[#0f172a]">
                    {earnings?.commission_rate || 70}% {isRTL ? "عمولة" : "Commission"}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-1">{isRTL ? "إجمالي الأرباح" : "Total Earned"}</p>
                    <p className="text-2xl font-bold text-white">{earnings?.total_earned?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-slate-500">{t("lyd")}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-1">{isRTL ? "مستحق الدفع" : "Pending"}</p>
                    <p className="text-2xl font-bold text-[#d4af37]">{earnings?.pending_amount?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-slate-500">{t("lyd")}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-1">{isRTL ? "تم الدفع" : "Paid"}</p>
                    <p className="text-2xl font-bold text-green-400">{earnings?.paid_amount?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-slate-500">{t("lyd")}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-slate-400 text-sm mb-1">{isRTL ? "إجمالي التوصيلات" : "Total Deliveries"}</p>
                    <p className="text-2xl font-bold text-white">{earnings?.total_deliveries || 0}</p>
                    <p className="text-xs text-slate-500">{isRTL ? "طلب" : "orders"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Earnings */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white text-base">{isRTL ? "آخر الأرباح" : "Recent Earnings"}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {earnings?.recent_earnings?.length > 0 ? (
                  <div className="divide-y divide-slate-700">
                    {earnings.recent_earnings.slice(0, 10).map((earning) => (
                      <div key={earning.id} className="flex items-center justify-between p-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${earning.status === 'paid' ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                            <DollarSign className={`h-4 w-4 ${earning.status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`} />
                          </div>
                          <div>
                            <p className="text-white text-sm">#{earning.order_id?.slice(0, 8)}</p>
                            <p className="text-slate-400 text-xs">{earning.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[#d4af37] font-bold">+{earning.amount?.toFixed(2)}</p>
                          <Badge className={earning.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                            {earning.status === 'paid' ? (isRTL ? 'مدفوع' : 'Paid') : (isRTL ? 'معلق' : 'Pending')}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-slate-400">
                    {isRTL ? "لا توجد أرباح بعد" : "No earnings yet"}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ ...actionDialog, open })}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>
              {actionDialog.action === "cancelled" && (isRTL ? "إلغاء الطلب" : "Cancel Order")}
              {actionDialog.action === "returned" && (isRTL ? "إرجاع الطلب" : "Return Order")}
              {actionDialog.action === "customer_not_found" && (isRTL ? "لم يتم العثور على العميل" : "Customer Not Found")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {actionLabels[actionDialog.action] || (isRTL ? "السبب" : "Reason")}
              </label>
              <Input
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={isRTL ? "أدخل السبب..." : "Enter reason..."}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-slate-600"
                onClick={() => setActionDialog({ open: false, order: null, action: null })}
              >
                {t("cancel")}
              </Button>
              <Button
                className="flex-1 bg-[#d4af37] text-[#0f172a]"
                onClick={confirmAction}
              >
                {t("confirm")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriverDashboard;
